/**
 * Admin — Security/Keys & Secrets (W1b-12 + W1b-13 backend-wired UI)
 *
 * Three sections:
 *   1. JWT signing keys     — RS256/ES256/EdDSA + 7-day rotation drain
 *   2. KMS / data encryption — AES256/AES128 + lazy re-encryption rotation
 *   3. TLS certificates     — upload PEM, view SAN/issuer/expiry, revoke
 *
 * All forms write through the real /admin/security/keys/* API surface
 * added in Wave 1b Stage 2. The visual primitives match the rest of the
 * Security page (SectionCard / typed-confirm-dialog / shadcn Button).
 *
 * v1 scope (intentional simplifications, documented in W1b-12-13-status):
 *   - JWT rotation default = RS256 (the picker is a v1.1 polish item).
 *   - Force-retire bypasses the 7-day drain — UI requires typed-confirm
 *     of the kid; API requires {confirmed: true} as the v1 stand-in for
 *     2-person approval. Real 2-person approval is W1b-04 territory.
 *   - KMS rotation is two-step in v1: rotate creates the new active +
 *     marks old as re_encrypting; complete-rotation finalizes. The v1.1
 *     worker auto-completes once it finishes re-encrypting the dataset.
 *   - TLS upload is a textarea pair (cert + key PEM). Drag-and-drop
 *     multipart upload is a v1.1 affordance.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import {
  FileKey,
  Database,
  ShieldCheck,
  RotateCw,
  XCircle,
  Plus,
  Trash2,
  Loader2,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { SectionCard } from '@/components/admin';
import { TypedConfirmDialog } from '@/components/admin/typed-confirm-dialog';
import { apiClient } from '@/lib/api-client';

// ─── Types matching backend response shapes ──────────────────────────────

interface JwtKey {
  id: string;
  kid: string;
  algorithm: 'RS256' | 'ES256' | 'EdDSA';
  status: 'active' | 'draining' | 'retired';
  publicKey: string;
  createdAt: string;
  activatedAt: string | null;
  drainingAt: string | null;
  retireAt: string | null;
  retiredAt: string | null;
}

interface KmsKey {
  id: string;
  alias: string;
  algorithm: 'AES256' | 'AES128';
  status: 'active' | 're_encrypting' | 'retired';
  rotationStartedAt: string | null;
  rotationCompletedAt: string | null;
  createdAt: string;
  retiredAt: string | null;
}

interface TlsCertificate {
  id: string;
  name: string;
  san: string[];
  notBefore: string;
  notAfter: string;
  issuer: string;
  serialNumber: string;
  usedByServices: string[];
  autoRenew: boolean;
  status: 'active' | 'expired' | 'revoked';
  daysToExpiry: number;
  createdAt: string;
}

export const Route = createFileRoute('/admin/security/keys')({
  component: KeysPage,
});

function KeysPage() {
  return (
    <div className="max-w-5xl mx-auto p-6 space-y-5">
      <div>
        <h1 className="text-xl font-semibold">Keys & Secrets</h1>
        <p className="text-xs text-muted-foreground mt-1">
          JWT signing keys, data encryption keys (KMS), and TLS certificates.
        </p>
      </div>

      <JwtKeysSection />
      <KmsKeysSection />
      <TlsCertificatesSection />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  JWT Signing Keys
// ═══════════════════════════════════════════════════════════════════════════

function JwtKeysSection() {
  const [keys, setKeys] = useState<JwtKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [retiring, setRetiring] = useState<JwtKey | null>(null);
  const [algorithm, setAlgorithm] = useState<'RS256' | 'ES256' | 'EdDSA'>(
    'RS256'
  );

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ keys: JwtKey[] }>(
        '/admin/security/keys/jwt'
      );
      setKeys(data.keys);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load JWT keys';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function rotate() {
    try {
      setRotating(true);
      await apiClient.post('/admin/security/keys/jwt/rotate', { algorithm });
      toast.success(
        'Key rotated — old key is draining for 7 days for in-flight tokens'
      );
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Rotation failed';
      toast.error(msg);
    } finally {
      setRotating(false);
    }
  }

  async function forceRetire(id: string) {
    try {
      await apiClient.post(`/admin/security/keys/jwt/${id}/force-retire`, {
        confirmed: true,
      });
      toast.success('Key retired — verification only');
      setRetiring(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Force-retire failed';
      toast.error(msg);
    }
  }

  if (loading) {
    return (
      <SectionCard title="JWT Signing Keys" icon={FileKey}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading keys…
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="JWT Signing Keys"
      icon={FileKey}
      description="Keys used to sign access tokens. Rotation drains the old key for 7 days so in-flight tokens stay valid."
      actions={
        <div className="flex items-center gap-1.5">
          <Select
            value={algorithm}
            onValueChange={(v) => setAlgorithm(v as 'RS256' | 'ES256' | 'EdDSA')}
          >
            <SelectTrigger className="h-7 text-xs w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="RS256">RS256</SelectItem>
              <SelectItem value="ES256">ES256</SelectItem>
              <SelectItem value="EdDSA">EdDSA</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs gap-1.5"
            onClick={rotate}
            disabled={rotating}
          >
            {rotating ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <RotateCw className="h-3 w-3" />
            )}
            Rotate active key
          </Button>
        </div>
      }
    >
      {keys.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed rounded-md px-6">
          <FileKey className="h-6 w-6 text-muted-foreground/50 mb-2" />
          <div className="text-xs font-medium">No DB-stored JWT signing keys</div>
          <p className="text-[10px] text-muted-foreground mt-1 max-w-md">
            Authentication is currently using the bootstrap signing key
            configured via environment variable (<code className="font-mono">JWT_PRIVATE_KEY_PATH</code> /
            <code className="font-mono"> JWT_PUBLIC_KEY_PATH</code>). DB-managed
            rotation is supported but not yet enabled for this tenant — click
            "Rotate active key" to generate a managed key. Once rotated, the
            DB-stored key takes precedence over the env-var bootstrap key.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {keys.map((k) => (
            <JwtKeyRow
              key={k.id}
              jwtKey={k}
              onForceRetire={() => setRetiring(k)}
            />
          ))}
        </div>
      )}

      {retiring && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setRetiring(null)}
          title="Force-retire signing key"
          description={`Retire ${retiring.kid} immediately, bypassing the 7-day drain window? Tokens signed by this key will start failing validation.`}
          consequences={[
            'Tokens signed by this key return 401 Unauthorized immediately',
            'JWKS endpoint stops exposing this key',
            'Operation cannot be reversed — only a fresh rotation will issue a new key',
          ]}
          confirmPhrase={retiring.kid}
          confirmLabel="Force-retire"
          variant="danger"
          onConfirm={() => forceRetire(retiring.id)}
        />
      )}
    </SectionCard>
  );
}

function JwtKeyRow({
  jwtKey,
  onForceRetire,
}: {
  jwtKey: JwtKey;
  onForceRetire: () => void;
}) {
  const ageDays = Math.floor(
    (Date.now() - new Date(jwtKey.createdAt).getTime()) / 86_400_000
  );
  const retireDays = jwtKey.retireAt
    ? Math.floor(
        (new Date(jwtKey.retireAt).getTime() - Date.now()) / 86_400_000
      )
    : null;

  return (
    <div
      className={cn(
        'border rounded-md p-3',
        jwtKey.status === 'active' && 'bg-card border-emerald-500/30',
        jwtKey.status === 'draining' && 'bg-amber-500/5 border-amber-500/30',
        jwtKey.status === 'retired' && 'bg-muted/30 opacity-70'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'h-8 w-8 rounded flex items-center justify-center shrink-0',
            jwtKey.status === 'active'
              ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
              : jwtKey.status === 'draining'
                ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                : 'bg-muted text-muted-foreground'
          )}
        >
          <FileKey className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium font-mono truncate">
              {jwtKey.kid}
            </span>
            <Badge variant="outline" className="h-4 text-[9px] px-1.5">
              {jwtKey.algorithm}
            </Badge>
            <Badge
              variant="outline"
              className={cn(
                'h-4 text-[9px] px-1.5',
                jwtKey.status === 'active' &&
                  'border-emerald-500/40 text-emerald-700 dark:text-emerald-300',
                jwtKey.status === 'draining' &&
                  'border-amber-500/40 text-amber-700 dark:text-amber-300',
                jwtKey.status === 'retired' && 'border-slate-400/40'
              )}
            >
              {jwtKey.status}
            </Badge>
          </div>
          <div className="mt-1 flex items-center gap-3 text-[10px] text-muted-foreground flex-wrap">
            <span className="tabular-nums">Age {ageDays}d</span>
            {jwtKey.status === 'draining' && retireDays !== null && (
              <span className="tabular-nums text-amber-600 dark:text-amber-400">
                {retireDays > 0
                  ? `Retires in ${retireDays}d`
                  : `Retire window passed`}
              </span>
            )}
            {jwtKey.retiredAt && (
              <span className="tabular-nums">
                Retired {new Date(jwtKey.retiredAt).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {(jwtKey.status === 'draining' || jwtKey.status === 'active') && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-[10px] gap-1 px-2 text-red-600 hover:text-red-700"
              onClick={onForceRetire}
              title="Force-retire (skip drain window)"
            >
              <XCircle className="h-3 w-3" />
              Force-retire
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  KMS / Data Encryption Keys
// ═══════════════════════════════════════════════════════════════════════════

function KmsKeysSection() {
  const [keys, setKeys] = useState<KmsKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [completing, setCompleting] = useState<string | null>(null);
  const [showRotate, setShowRotate] = useState(false);
  const [aliasDraft, setAliasDraft] = useState('');
  const [algorithmDraft, setAlgorithmDraft] = useState<'AES256' | 'AES128'>(
    'AES256'
  );

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ keys: KmsKey[] }>(
        '/admin/security/keys/kms'
      );
      setKeys(data.keys);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load KMS keys';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function rotate() {
    if (!aliasDraft.trim()) {
      toast.error('Alias is required');
      return;
    }
    try {
      setRotating(true);
      await apiClient.post('/admin/security/keys/kms/rotate', {
        alias: aliasDraft.trim(),
        algorithm: algorithmDraft,
      });
      toast.success(
        'KMS rotation started — old key marked re_encrypting until completion'
      );
      setShowRotate(false);
      setAliasDraft('');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Rotation failed';
      toast.error(msg);
    } finally {
      setRotating(false);
    }
  }

  async function complete(id: string) {
    try {
      setCompleting(id);
      await apiClient.post(
        `/admin/security/keys/kms/${id}/complete-rotation`,
        {}
      );
      toast.success('Rotation completed — key retired');
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Complete failed';
      toast.error(msg);
    } finally {
      setCompleting(null);
    }
  }

  if (loading) {
    return (
      <SectionCard title="Data Encryption Keys (KMS)" icon={Database}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading KMS keys…
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="Data Encryption Keys (KMS)"
      icon={Database}
      description="Customer-managed keys for data at rest. Rotation is lazy: new writes use the new key; old data is re-encrypted on next access."
      actions={
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1.5"
          onClick={() => setShowRotate(true)}
        >
          <RotateCw className="h-3 w-3" />
          Rotate / new
        </Button>
      }
    >
      {keys.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center border border-dashed rounded-md">
          <Database className="h-6 w-6 text-muted-foreground/50 mb-2" />
          <div className="text-xs font-medium">No KMS keys</div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Rotate / new starts the first key for an alias.
          </p>
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '28%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '22%' }} />
              </colgroup>
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Alias
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Algorithm
                  </th>
                  <th className="px-3 py-2 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Created
                  </th>
                  <th className="px-3 py-2 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b hover:bg-muted/20">
                    <td className="px-3 py-2">
                      <div className="text-xs font-mono truncate">{k.alias}</div>
                    </td>
                    <td className="px-3 py-2 text-[10px] font-mono">
                      {k.algorithm}
                    </td>
                    <td className="px-3 py-2">
                      <Badge
                        variant="outline"
                        className={cn(
                          'h-4 text-[9px] px-1.5',
                          k.status === 'active' &&
                            'border-emerald-500/40 text-emerald-700 dark:text-emerald-300',
                          k.status === 're_encrypting' &&
                            'border-amber-500/40 text-amber-700 dark:text-amber-300',
                          k.status === 'retired' && 'border-slate-400/40'
                        )}
                      >
                        {k.status}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-xs tabular-nums text-right">
                      {new Date(k.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {k.status === 're_encrypting' && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-[10px] gap-1 px-2"
                          onClick={() => complete(k.id)}
                          disabled={completing === k.id}
                          title="Mark rotation complete (key retires)"
                        >
                          {completing === k.id ? (
                            <Loader2 className="h-2.5 w-2.5 animate-spin" />
                          ) : (
                            <CheckCircle2 className="h-2.5 w-2.5" />
                          )}
                          Complete
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <Dialog open={showRotate} onOpenChange={setShowRotate}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Rotate / new KMS key</DialogTitle>
            <DialogDescription>
              Starts a new key for the alias. If a prior key existed, it flips
              to <code>re_encrypting</code> until you complete the rotation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label htmlFor="kms-alias" className="text-xs">
                Alias
              </Label>
              <Input
                id="kms-alias"
                value={aliasDraft}
                onChange={(e) => setAliasDraft(e.target.value)}
                placeholder="data-at-rest"
                className="h-8 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Algorithm</Label>
              <Select
                value={algorithmDraft}
                onValueChange={(v) =>
                  setAlgorithmDraft(v as 'AES256' | 'AES128')
                }
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AES256">AES256</SelectItem>
                  <SelectItem value="AES128">AES128</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRotate(false)}
              disabled={rotating}
            >
              Cancel
            </Button>
            <Button size="sm" onClick={rotate} disabled={rotating}>
              {rotating ? 'Rotating…' : 'Rotate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  TLS Certificates
// ═══════════════════════════════════════════════════════════════════════════

function TlsCertificatesSection() {
  const [certs, setCerts] = useState<TlsCertificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [revoking, setRevoking] = useState<TlsCertificate | null>(null);

  const refresh = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.get<{ certificates: TlsCertificate[] }>(
        '/admin/security/keys/tls'
      );
      setCerts(data.certificates);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load TLS certs';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function revoke(id: string) {
    try {
      await apiClient.delete(`/admin/security/keys/tls/${id}`);
      toast.success('Certificate revoked');
      setRevoking(null);
      await refresh();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Revoke failed';
      toast.error(msg);
    }
  }

  if (loading) {
    return (
      <SectionCard title="TLS Certificates" icon={ShieldCheck}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground p-4">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading certificates…
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      title="TLS Certificates"
      icon={ShieldCheck}
      description="Server certificates protecting public endpoints. Auto-renewal hook is recorded; the corporate-CA worker lands in W4."
      actions={
        <Button
          size="sm"
          variant="outline"
          className="h-7 text-xs gap-1.5"
          onClick={() => setShowUpload(true)}
        >
          <Plus className="h-3 w-3" />
          Upload PEM
        </Button>
      }
    >
      {certs.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center border border-dashed rounded-md">
          <ShieldCheck className="h-6 w-6 text-muted-foreground/50 mb-2" />
          <div className="text-xs font-medium">No certificates installed</div>
          <p className="text-[10px] text-muted-foreground mt-1">
            Upload a cert + key PEM to register the first certificate.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {certs.map((c) => (
            <CertificateRow
              key={c.id}
              cert={c}
              onRevoke={() => setRevoking(c)}
            />
          ))}
        </div>
      )}

      <UploadCertDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        onUploaded={refresh}
      />

      {revoking && (
        <TypedConfirmDialog
          open
          onOpenChange={(v) => !v && setRevoking(null)}
          title="Revoke certificate"
          description={`Revoke ${revoking.name}? Services using this cert will lose TLS until replaced.`}
          consequences={[
            'Status flips to "revoked" — cert remains visible for audit',
            `Services in used-by reverse map will lose TLS: ${revoking.usedByServices.join(', ') || '(none)'}`,
            'Renewal can only restore service via a fresh upload',
          ]}
          confirmPhrase={revoking.name}
          confirmLabel="Revoke"
          variant="danger"
          onConfirm={() => revoke(revoking.id)}
        />
      )}
    </SectionCard>
  );
}

function CertificateRow({
  cert,
  onRevoke,
}: {
  cert: TlsCertificate;
  onRevoke: () => void;
}) {
  const days = cert.daysToExpiry;
  const isExpired = days < 0;
  const isExpiring = days >= 0 && days < 30;

  return (
    <div
      className={cn(
        'border rounded-md p-3',
        isExpired && 'border-red-500/30 bg-red-500/5',
        isExpiring && 'border-amber-500/30 bg-amber-500/5',
        cert.status === 'revoked' && 'opacity-60'
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            'h-8 w-8 rounded flex items-center justify-center shrink-0',
            isExpired || cert.status === 'revoked'
              ? 'bg-red-500/15 text-red-600 dark:text-red-400'
              : isExpiring
                ? 'bg-amber-500/15 text-amber-600 dark:text-amber-400'
                : 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
          )}
        >
          <ShieldCheck className="h-3.5 w-3.5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium truncate">{cert.name}</span>
            <Badge
              variant="outline"
              className={cn(
                'h-4 text-[9px] px-1.5',
                cert.status === 'active' &&
                  'border-emerald-500/40 text-emerald-700 dark:text-emerald-300',
                cert.status === 'expired' &&
                  'border-red-500/40 text-red-700 dark:text-red-300',
                cert.status === 'revoked' && 'border-slate-400/40'
              )}
            >
              {cert.status}
            </Badge>
            {cert.autoRenew && (
              <Badge variant="outline" className="h-4 text-[9px] px-1.5">
                auto-renew
              </Badge>
            )}
            <span
              className={cn(
                'text-[10px] tabular-nums',
                isExpired && 'text-red-600 dark:text-red-400 font-medium',
                isExpiring && 'text-amber-600 dark:text-amber-400',
                !isExpired && !isExpiring && 'text-muted-foreground'
              )}
            >
              {isExpired
                ? `Expired ${-days}d ago`
                : `Expires in ${days}d`}
            </span>
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground truncate font-mono">
            {cert.issuer}
          </div>
          {cert.san.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {cert.san.slice(0, 3).map((san) => (
                <code
                  key={san}
                  className="text-[10px] font-mono bg-muted/50 px-1.5 py-0.5 rounded"
                >
                  {san}
                </code>
              ))}
              {cert.san.length > 3 && (
                <code className="text-[10px] text-muted-foreground px-1">
                  +{cert.san.length - 3} more
                </code>
              )}
            </div>
          )}
        </div>
        {cert.status === 'active' && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-[10px] gap-1 px-2 text-red-600 hover:text-red-700"
            onClick={onRevoke}
            title="Revoke certificate"
          >
            <Trash2 className="h-3 w-3" />
            Revoke
          </Button>
        )}
      </div>
    </div>
  );
}

function UploadCertDialog({
  open,
  onOpenChange,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onUploaded: () => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [certPem, setCertPem] = useState('');
  const [keyPem, setKeyPem] = useState('');
  const [usedBy, setUsedBy] = useState('');
  const [autoRenew, setAutoRenew] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  function reset() {
    setName('');
    setCertPem('');
    setKeyPem('');
    setUsedBy('');
    setAutoRenew(true);
  }

  async function submit() {
    if (!name.trim() || !certPem.trim() || !keyPem.trim()) {
      toast.error('Name, certificate PEM, and private key PEM are all required.');
      return;
    }
    try {
      setSubmitting(true);
      await apiClient.post('/admin/security/keys/tls', {
        name: name.trim(),
        certificatePem: certPem,
        privateKeyPem: keyPem,
        usedByServices: usedBy
          .split(',')
          .map((s) => s.trim())
          .filter((s) => s.length > 0),
        autoRenew,
      });
      toast.success('Certificate uploaded');
      reset();
      onOpenChange(false);
      await onUploaded();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Upload failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Upload TLS certificate</DialogTitle>
          <DialogDescription>
            Paste the certificate and private key in PEM format. SAN, issuer,
            and expiry are extracted automatically on upload.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <Label htmlFor="tls-name" className="text-xs">
              Name
            </Label>
            <Input
              id="tls-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="webhooks-mtls"
              className="h-8 text-xs"
            />
          </div>
          <div>
            <Label htmlFor="tls-cert" className="text-xs">
              Certificate PEM
            </Label>
            <Textarea
              id="tls-cert"
              value={certPem}
              onChange={(e) => setCertPem(e.target.value)}
              placeholder="-----BEGIN CERTIFICATE-----..."
              className="font-mono text-[10px] min-h-[100px]"
            />
          </div>
          <div>
            <Label htmlFor="tls-key" className="text-xs">
              Private key PEM
            </Label>
            <Textarea
              id="tls-key"
              value={keyPem}
              onChange={(e) => setKeyPem(e.target.value)}
              placeholder="-----BEGIN PRIVATE KEY-----..."
              className="font-mono text-[10px] min-h-[100px]"
            />
          </div>
          <div>
            <Label htmlFor="tls-usedby" className="text-xs">
              Used by services (comma-separated)
            </Label>
            <Input
              id="tls-usedby"
              value={usedBy}
              onChange={(e) => setUsedBy(e.target.value)}
              placeholder="webhooks-mtls, caddy"
              className="h-8 text-xs"
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="tls-renew"
              checked={autoRenew}
              onCheckedChange={setAutoRenew}
            />
            <Label htmlFor="tls-renew" className="text-xs cursor-pointer">
              Enable auto-renew (corporate-CA worker, W4)
            </Label>
          </div>
          <div className="flex items-start gap-2 p-2 bg-muted/40 rounded text-[10px]">
            <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0 mt-0.5" />
            <span>
              Private keys are stored alongside the cert in v1. KMS-wrapping is
              a v1.1 follow-up tied to the KMS keys surface.
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              reset();
              onOpenChange(false);
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button size="sm" onClick={submit} disabled={submitting}>
            {submitting ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
